import { useCallback, useContext } from "react"
import { Address } from "viem"
import { useWalletClient } from "wagmi"

import {
  RootState,
  selectDepositByDepositId,
  selectTokenDecimals,
  useDappSelector,
} from "#/redux-state"
import { ETHEREUM_CHAIN_ID } from "#/shared/constants"
import Portal from "#/shared/constants/artifacts/Portal"
import { FlowContext } from "#/shared/context"

import { useWriteContractAndWaitForTransaction } from "../contract"

import useLatestBridgeTransaction from "./useLatestBridgeTransaction"

export default function useBridgeDeposit(
  token: Address,
  amount: bigint,
  depositId?: number,
) {
  const { writeContractAsync, ...rest } =
    useWriteContractAndWaitForTransaction(ETHEREUM_CHAIN_ID)

  const { setLatestBridgeTransaction } = useLatestBridgeTransaction()

  const { data: walletClient } = useWalletClient()

  const { isBitcoinFlow } = useContext(FlowContext)

  const deposit = useDappSelector((state: RootState) =>
    selectDepositByDepositId(state, depositId),
  )

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, deposit?.token ?? "tBTC"),
  )

  const bridgeDeposit = useCallback(
    async (options?: {
      onError?: (error: unknown) => void
      onSuccess?: () => void
    }) => {
      if (!deposit || !depositId) {
        throw new Error("Deposit id not defined")
      }

      const transactionHash = await writeContractAsync(
        {
          abi: Portal.abi,
          address: Portal.address,
          functionName: "bridgeDeposit",
          args: [token, depositId],
          chainId: ETHEREUM_CHAIN_ID,
          // If this is a Bitcoin flow, we do not want to simulate the contract
          // as this will most likely fail since the associated EVM address has
          // zero balance on a given chain.
          __mode: isBitcoinFlow ? "prepared" : undefined,
          // We should use the wallet client object because it has
          // `sendTransaction` function, which will be fired. The
          // orangekit/passport lib creates a custom wallet client with an
          // overridden `sendTransaction` function. It sends the transaction via
          // Orangekit smart contracts.
          account: isBitcoinFlow ? walletClient?.account : undefined,
        },
        options,
      )

      setLatestBridgeTransaction(
        deposit.token === "BTC" ? null : amount,
        deposit.token,
        Number(tokenDecimals),
        transactionHash,
      )
    },
    [
      writeContractAsync,
      setLatestBridgeTransaction,
      amount,
      token,
      deposit,
      tokenDecimals,
      depositId,
      walletClient,
      isBitcoinFlow,
    ],
  )

  return {
    ...rest,
    bridgeDeposit,
  }
}
